/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { Divider, Autocomplete, TextField } from "@mui/material";
import Box from "components/generals/Box";
import { useState, useMemo } from "react";
import DefaultDashboardLayout from "layouts/dashboard/DefaultDashboardLayout";
import NewPatientDataContent from "components/content/NewPatientDataContent";
import NewUserDataContent from "components/content/NewUserDataContent";
import NewDoctorDataContent from "components/content/NewDoctorContent";
import { UserRolesEnum } from "medicalsystem-shared";
import { useSelector } from "react-redux";
import { RootState } from "store/store";
import { selectUserRoles } from "store/slices/auth.slice";
import { getCreatableRoles } from "utils/rolePermissions.utils";

/**
 * Universal user creation page with role-based form selection.
 * Provides dynamic form rendering based on selected user role.
 * 
 * Features:
 * - User role selection dropdown with hierarchical access control
 * - Only shows roles that current user can create (based on role hierarchy)
 * - Dynamic form rendering based on selected role:
 *   - PATIENT: Full patient registration form
 *   - DOCTOR: Doctor/provider registration
 *   - NURSE/HEAD_NURSE: Nurse registration with role variant
 *   - ADMIN/ROOT/MAINTAINER/SECRETARY_RECEPTIONIST: Basic user form
 * - Role-specific validation and required fields
 * - Form switching without page reload
 * - Integration with all user management systems
 * 
 * Role Hierarchy (what each role can create):
 * - ROOT: Can create all roles including ROOT
 * - ADMIN: Can create all roles except ROOT
 * - MAINTAINER: Can create MAINTAINER and below
 * - DOCTOR/HEAD_NURSE/NURSE/SECRETARY_RECEPTIONIST: Can create self and PATIENT
 * - PATIENT: Cannot create users
 * 
 * User Types:
 * - Patient: Requires demographic, insurance, medical history
 * - Doctor: Requires credentials, specialties, licenses
 * - Nurse: Requires certifications, assignments
 * - Head Nurse: Additional administrative permissions
 * - Admin/Staff: Basic user information and permissions
 * 
 * @returns {JSX.Element} Universal user creation page
 */
const NewUserPage: React.FC = () => {
  const [userType, setUserType] = useState(null);
  const currentUserRoles = useSelector((state: RootState) => selectUserRoles(state));
  const creatableRoles = useMemo(() => getCreatableRoles(currentUserRoles), [currentUserRoles]);

  return (
    <DefaultDashboardLayout>
      <Box className="form-container" borderRadius="lg" p={3} mb={4} shadow="md" id="cy-useridFormContainer">
        <Autocomplete
          disablePortal
          id="user-type-select"
          options={creatableRoles}
          sx={{ width: 300 }}
          onChange={(_, newValue) => {
            setUserType(newValue);
          }}
          renderInput={(params) => <TextField {...params} label="User Type" />}
        />
        <Divider />
        {userType === UserRolesEnum.PATIENT && <NewPatientDataContent />}
        {userType === UserRolesEnum.DOCTOR && <NewDoctorDataContent isDoctor={true} isHeadNurse={false}/>}
        {[UserRolesEnum.NURSE, UserRolesEnum.HEAD_NURSE].includes(userType as UserRolesEnum) && <NewDoctorDataContent isDoctor={false} isHeadNurse={userType === UserRolesEnum.HEAD_NURSE} />}
        {[UserRolesEnum.ADMIN, UserRolesEnum.ROOT, UserRolesEnum.MAINTAINER, UserRolesEnum.SECRETARY_RECEPTIONIST].includes(userType as UserRolesEnum) && <NewUserDataContent type={userType} />}
      </Box>
    </DefaultDashboardLayout>
  );
};

export default NewUserPage;

